import { Stack } from '@mui/material';
import InputAdornment from '@mui/material/InputAdornment';
import { FormHelperText } from '@portals/core/src/components/FormHelperText/FormHelperText';
import { TextField, TextFieldProps } from '@portals/core/src/components/TextField/TextField';
import { Field, getIn } from 'formik';
import i18next from 'i18next';
import React, { useCallback, useMemo, useState } from 'react';

import { useTextValidator } from '../../hooks/TextValidator/TextValidator';
import { useFormField } from '../../hooks/useFormField/useFormField';
import { teeEvent } from '../../utilties/teeEvent';

type InputMode = TextFieldProps['inputMode'];
type InputType = TextFieldProps['type'];

// TODO: color and size are excluded, otherwise the type is not compatible with useAutocomplete

// eslint-disable-next-line import/no-named-as-default-member
const fixedT = i18next.getFixedT.bind(i18next)(null, 'forms', 'text');

export type TextInputProps = Omit<TextFieldProps, 'name' | 'value' | 'pattern' | 'color' | 'size'> & {
  name: TextFieldProps['name'];
  helperText?: TextFieldProps['helperText'];
  inputMode?: InputMode;
  type?: InputType;
  placeholder?: TextFieldProps['placeholder'];
  required?: TextFieldProps['required'];
  min?: number;
  max?: number;
  beforeIcon?: React.ReactNode;
  t?: (key: string, options?: Record<string, unknown>) => string;
  textCount?: boolean;
  disableExtendedTextCount?: boolean;
};

interface HelperTextProps {
  value: string | undefined;
  textCount?: boolean;
  max?: number;
  helperText: React.ReactNode;
  errorMessage: string;
  t: (key: string, options?: Record<string, unknown>) => string;
  disableExtendedTextCount: boolean;
}

const HelperText = ({
  value,
  max,
  textCount,
  helperText,
  errorMessage,
  t,
  disableExtendedTextCount,
}: HelperTextProps) => {
  const count = value?.length ?? 0;

  if (textCount) {
    const isExtended = max && !disableExtendedTextCount;
    return (
      <Stack direction="row" justifyContent="space-between">
        <FormHelperText component="div">{errorMessage || helperText}</FormHelperText>
        <FormHelperText component="div">
          {isExtended ? t('charsMax', { count, max }) : t('chars', { count })}
        </FormHelperText>
      </Stack>
    );
  }

  if (React.isValidElement(helperText)) {
    return helperText;
  }

  if (errorMessage || helperText) {
    return <FormHelperText error>{errorMessage || helperText}</FormHelperText>;
  }

  return null;
};

/**
 * TextInput Form Field
 */
export const TextInput = React.forwardRef<HTMLInputElement, TextInputProps>(
  (
    {
      name,
      helperText,
      type = 'text' as InputType,
      inputMode = 'text' as InputMode,
      placeholder = '',
      required = false,
      min,
      max,
      beforeIcon,
      t = fixedT,
      textCount = false,
      disableExtendedTextCount = false,
      slotProps,
      ...props
    },
    ref
  ) => {
    const validate = useTextValidator({ required, min, max, t });
    const [value] = useFormField<string>(name);
    const [focused, setFocused] = useState(false);

    const onFocusCallback = useCallback(() => setFocused(true), []);
    const onBlurCallback = useCallback(() => setFocused(false), []);

    const fieldComponent = useCallback(
      ({ form, field, slotProps, ...props }) => {
        const hasError = getIn(form.touched, name) && Boolean(getIn(form.errors, name));
        const errorMessage = hasError ? getIn(form.errors, name) : '';
        const hasTextCountError = textCount && max && field.value?.length > max;

        const onBlur = teeEvent(props.onBlur, field.onBlur, onBlurCallback);
        const onFocus = teeEvent(props.onFocus, field.onFocus, onFocusCallback);
        const onChange = teeEvent(props.onChange, field.onChange);

        return (
          <TextField
            error={hasError || hasTextCountError}
            {...field}
            {...props}
            slotProps={{
              formHelperText: {
                component: HelperText,
                errorMessage,
                helperText,
                value: field.value,
                max,
                t,
                textCount,
                disableExtendedTextCount,
              },
              ...slotProps,
            }}
            helperText={' '}
            onFocus={onFocus}
            onBlur={onBlur}
            onChange={onChange}
          />
        );
      },
      [name, onBlurCallback, onFocusCallback, textCount, helperText, max, t]
    );

    const InputProps = useMemo(
      () => (beforeIcon ? { startAdornment: <InputAdornment position="end">{beforeIcon}</InputAdornment> } : undefined),
      [beforeIcon]
    );

    const inputProps = useMemo(
      () => ({
        inputMode,
        type,
        required,
        min,
        max,
      }),
      [inputMode, max, min, required, type]
    );

    const InputLabelProps = useMemo(() => {
      return beforeIcon
        ? {
            shrink: focused || !!value,
            style: { marginLeft: focused || value ? 0 : 40 },
          }
        : {
            shrink: focused || !!value,
            style: { marginLeft: 0 },
          };
    }, [beforeIcon, focused, value]);

    return (
      <Field
        component={fieldComponent}
        name={name}
        validate={validate}
        placeholder={placeholder}
        required={required}
        slotProps={{
          htmlInput: inputProps,
          input: InputProps,
          inputLabel: InputLabelProps,
          ...slotProps,
        }}
        inputRef={ref}
        {...props}
      />
    );
  }
);

TextInput.displayName = 'TextInput';
